import { LiquidPill } from "components/ui/v2";
import { useQuery } from "@apollo/client";
import { CelebrationModalV2 } from "components/ui/v2/explore/CelebrationModalV2";
import { gqlUser } from "gql";
import { Cake } from "lucide-react";
import moment from "moment";
import useTranslation from "next-translate/useTranslation";
import React from "react";
import UserAvatar from "components/User/UserAvatar";
import { IUser } from "types/User.types";
import SideCard from "../SideCard/SideCard";
import SideCardV2 from "../SideCard/SideCardV2";
import UserItem from "../SideCard/UserItem";
import {
  isCacheValid,
  useExploreWidgetsCacheRefresh,
  useExploreWidgetsStore
} from "stores/explore/useExploreWidgetsStore";

interface BirthdayWidgetProps {
  readonly variant?: "default" | "v2";
}

const BirthdayWidget = (props: BirthdayWidgetProps) => {
  const { variant = "default" } = props;
  const [currentBirthdays, setCurrentBirthdays] = React.useState<IUser[]>([]);
  const [followingBirthdays, setFollowingBirthdays] = React.useState<IUser[]>(
    []
  );
  const [celebrationOpen, setCelebrationOpen] = React.useState(false);

  const { t } = useTranslation("celebration");

  const { birthdays, setBirthdays, hasHydrated } = useExploreWidgetsStore();
  useExploreWidgetsCacheRefresh();
  const hasValidCache = isCacheValid(birthdays.lastFetched);

  const { loading: birthdaysLoading } = useQuery<{
    usersWithBirthday: IUser[];
  }>(gqlUser.queries.GET_USERS_BIRTHDAY, {
    fetchPolicy: "network-only",
    skip: !hasHydrated || hasValidCache,
    onCompleted: (data) => {
      if (data?.usersWithBirthday) {
        setBirthdays(data.usersWithBirthday);
      }
    }
  });

  React.useEffect(() => {
    if (!birthdays.data) return;

    const current = birthdays.data.filter((user: IUser) => user.isBirthday);
    const following = birthdays.data.filter((user: IUser) => !user.isBirthday);

    setCurrentBirthdays(current);

    const birthDayThisMonth = following.filter((user: IUser) => {
      const birthday = user.birthday ?? user.birthDate;

      return moment(birthday).month() === moment().month();
    });

    const birthDayNextMonth = following.filter((user: IUser) => {
      const birthday = user.birthday ?? user.birthDate;

      return moment(birthday).month() === (moment().month() + 1) % 12;
    });

    setFollowingBirthdays([...birthDayThisMonth, ...birthDayNextMonth]);
  }, [birthdays.data]);

  const renderItems = (users: IUser[]) =>
    users.map((user) => (
      <div key={`${user.id}_birthday_card`}>
        <UserItem user={user} birthdayCard compact={variant === "v2"} />
      </div>
    ));

  const ActiveSideCard = (
    variant === "v2" ? SideCardV2 : SideCard
  ) as typeof SideCard;

  // No mostrar la card si hoy no hay cumpleaños.
  if (!birthdaysLoading && currentBirthdays.length === 0) {
    return null;
  }

  const initialLoading = birthdaysLoading && !birthdays.data;

  return (
    <>
    <ActiveSideCard
      title={t("Cumpleaños")}
      loading={initialLoading}
      buttonText={currentBirthdays.length > 3 ? t("Ver todos") : ""}
      onViewMore={() => setCelebrationOpen(true)}
      defaultExpanded
      collapsible={variant === "default" ? true : false}
      variant={variant}
      icon={
        variant === "v2" ? (
          <Cake size={16} strokeWidth={1} color="#0073FF" />
        ) : null
      }
      headerTag={
        variant === "v2" ? (
          <LiquidPill
            variant="emerald"
            appearance="tinted"
            shadow="neutral"
            size="xxs"
            text={t("Hoy")}
          />
        ) : undefined
      }
    >
      {currentBirthdays?.length > 0 && (
        <>
          {variant === "v2" ? (
            <div className="mt-1 space-y-2">
              {currentBirthdays.slice(0, 3).map((birthdayUser) => {
                const birthdayDate = moment(
                  birthdayUser.birthday ?? birthdayUser.birthDate
                );
                const birthdayLabel = birthdayDate.isValid()
                  ? birthdayDate.format("DD MMM")
                  : t("Hoy");

                return (
                  <div
                    key={birthdayUser.id}
                    className="flex min-w-0 items-center justify-between gap-3 rounded-2xl px-2 py-1.5"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-semibold leading-tight text-slate-900 dark:text-zinc-100">
                        🎂{" "}
                        {[birthdayUser.name, birthdayUser.lastname]
                          .filter(Boolean)
                          .join(" ")}
                      </p>
                      <p className="mt-0.5 truncate text-[11px] text-slate-500 dark:text-zinc-400">
                        {t("Cumpleaños")} · {birthdayLabel}
                      </p>
                    </div>

                    <div className="shrink-0 rounded-full ring-2 ring-white/90 ring-offset-2 ring-offset-white/60 dark:ring-zinc-900 dark:ring-offset-zinc-800">
                      <UserAvatar user={birthdayUser} size={54} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="my-2">
              {renderItems(currentBirthdays.slice(0, 3))}
            </div>
          )}
        </>
      )}

      {/* Intencional: en cumpleaños mostramos solo los de hoy dentro de la card. */}
    </ActiveSideCard>
    <CelebrationModalV2
      open={celebrationOpen}
      onClose={() => setCelebrationOpen(false)}
      current={currentBirthdays}
      following={followingBirthdays}
      birthdayCard
    />
    </>
  );
};

export default BirthdayWidget;
